import {
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives";
import UFOSegment from "@atlaskit/react-ufo/segment";

import {
    Adg4Button,
    Adg4ButtonProps
} from "@/components/adg4/button/src/button";
import { Row } from "@/libraries/wpl/foundations/src/grid";
import { TypographyStyles } from "@/libraries/wpl/foundations/src/typography-styles";

import { CustomerStoryFilterModalBasic } from "../components/customer-story-filter-modal-basic";
import { CustomerStoryFilterModalComplex } from "../components/customer-story-filter-modal-complex";
import { CustomerStorySearchBar } from "../components/customer-story-search-bar";
import {
    commitAllStagedActiveValues,
    getActiveFiltersByFilterType,
    getNumberOfCustomerStoryColumns,
    resetAllStagedActiveToCommittedActiveValue,
    setAllStagedActiveToFalse,
    useWindowDimensions
} from "../utils/helpers";
import { LocalizedFilterOptions } from "../utils/localized-filter-options";
import {
    CustomerStoryFilterObject,
    FilterCategories,
    FilterOptions,
    SubFilterObject
} from "../utils/types";

interface CustomerStoryFiltersProps {
    filterObject: CustomerStoryFilterObject;
    numberOfCustomerStoriesByStagedValues: number;
    numberOfCustomerStoriesByCommittedValues: number;
    searchStringValue: string;
    searchInputFieldFocused: boolean;
    hasFilterSearchRun: boolean;
    setSearchInputFieldFocused: Dispatch<SetStateAction<boolean>>;
    setFilterObjectValue: Dispatch<SetStateAction<CustomerStoryFilterObject>>;
    setSearchString: (searchString: string) => void;
    clearAllFilterButtonClickHandler: () => void;
    clearSearchField: () => void;
    runSearchWithFilters: (
        filterObject: CustomerStoryFilterObject,
        searchString: string
    ) => void;
}

type WhichFilterModalIsOpen = {
    [key in FilterCategories]: boolean;
};

export const CustomerStoryFilters = ({
    filterObject,
    numberOfCustomerStoriesByCommittedValues,
    numberOfCustomerStoriesByStagedValues,
    searchStringValue,
    searchInputFieldFocused,
    hasFilterSearchRun,
    setFilterObjectValue,
    setSearchInputFieldFocused,
    setSearchString,
    clearAllFilterButtonClickHandler,
    clearSearchField,
    runSearchWithFilters
}: CustomerStoryFiltersProps) => {
    const intl = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const localizedMap = LocalizedFilterOptions();

    const whichFilterModalIsOpenObjectDefaultState: WhichFilterModalIsOpen = {
        "solution": false,
        "product": false,
        "industry": false,
        "more filters": false
    };

    const { width } = useWindowDimensions();

    Object.freeze(whichFilterModalIsOpenObjectDefaultState);

    const [whichFilterModalIsOpenObject, updateWhichFilterModalIsOpenObject] =
        useState<WhichFilterModalIsOpen>(
            whichFilterModalIsOpenObjectDefaultState
        );

    // Updates the state of which filter modal is open
    // If a modal is closing, we commit all the stagedActive values
    const updateWhichFilterModalIsShown = (subFilter: FilterCategories) => {
        const isModalClosing = Object.keys(whichFilterModalIsOpenObject).filter(
            (key) => whichFilterModalIsOpenObject[key as FilterCategories]
        );

        updateWhichFilterModalIsOpenObject(() => ({
            ...whichFilterModalIsOpenObjectDefaultState,
            [subFilter]: !whichFilterModalIsOpenObject[subFilter]
        }));

        if (isModalClosing.length > 0) {
            setFilterObjectValue((prevState: CustomerStoryFilterObject) => {
                filterObject = {
                    ...prevState,
                    ...commitAllStagedActiveValues(prevState)
                };
                runSearchWithFilters(filterObject, searchStringValue);
                return filterObject;
            });
        }
    };

    // Updates the stagedActive value of the filter
    const updateSubFilterStagedValue = (
        subFilterName: FilterCategories,
        filterOptionName: string
    ) => {
        setFilterObjectValue((prevState: CustomerStoryFilterObject) => {
            return {
                ...prevState,
                [subFilterName]: {
                    ...prevState[subFilterName],
                    [filterOptionName]: {
                        ...prevState[subFilterName][filterOptionName],
                        stagedActive: !(
                            prevState[subFilterName][
                                filterOptionName
                            ] as FilterOptions
                        ).stagedActive
                    }
                }
            };
        });
    };

    const clearAllStagedValues = () => {
        setFilterObjectValue((prevState: CustomerStoryFilterObject) => {
            return {
                ...prevState,
                ...setAllStagedActiveToFalse(prevState)
            };
        });
    };

    // Clears all stagedActive values in the more filters section
    const clearMoreFilterStagedValues = () => {
        setFilterObjectValue((prevState: CustomerStoryFilterObject) => {
            const updatedFilterState = Object.entries(
                prevState["more filters"]
            ).reduce(
                (
                    acc: SubFilterObject,
                    [filterOptionName, filterOptionValue]
                ) => {
                    acc[filterOptionName] = {
                        ...filterOptionValue,
                        stagedActive: false
                    };
                    return acc;
                },
                {}
            );
            return {
                ...prevState,
                ["more filters"]: {
                    ...prevState["more filters"],
                    ...updatedFilterState
                }
            };
        });
    };

    // resets all stagedActive values to their committedActive value
    const resetAllStagedValues = () => {
        setFilterObjectValue((prevState: CustomerStoryFilterObject) => {
            const updatedFilterState =
                resetAllStagedActiveToCommittedActiveValue(prevState);
            return {
                ...prevState,
                ...updatedFilterState
            };
        });
    };

    // Closes complex modal and runs search
    const runSearchWithFiltersWrapper = () => {
        updateWhichFilterModalIsShown("more filters");

        runSearchWithFilters(filterObject, searchStringValue);
    };

    // Handles logic for determining which filter modal to show
    const getFilterModal = (
        showFilter: boolean,
        filterKey: FilterCategories
    ) => {
        if (!showFilter || !whichFilterModalIsOpenObject[filterKey]) {
            return null;
        }

        const numCols = getNumberOfCustomerStoryColumns(width);

        if (numCols === 3) {
            if (filterKey === "more filters") {
                return (
                    <CustomerStoryFilterModalComplex
                        numberOfCustomerStories={
                            numberOfCustomerStoriesByStagedValues
                        }
                        filterObject={filterObject}
                        onlyShowMoreFiltersCategory={true}
                        updateFilterObject={updateSubFilterStagedValue}
                        closeModal={() =>
                            updateWhichFilterModalIsShown("more filters")
                        }
                        resetFilters={resetAllStagedValues}
                        clearMoreFilters={clearMoreFilterStagedValues}
                        clearFilters={clearAllStagedValues}
                        runSearch={runSearchWithFiltersWrapper}
                    />
                );
            } else {
                return (
                    <CustomerStoryFilterModalBasic
                        subFilter={filterKey}
                        subFilterObject={filterObject[filterKey]}
                        updateFilterObject={(filterOptionName: string) =>
                            updateSubFilterStagedValue(
                                filterKey,
                                filterOptionName
                            )
                        }
                    />
                );
            }
        } else {
            return (
                <CustomerStoryFilterModalComplex
                    numberOfCustomerStories={
                        numberOfCustomerStoriesByStagedValues
                    }
                    filterObject={filterObject}
                    onlyShowMoreFiltersCategory={false}
                    updateFilterObject={updateSubFilterStagedValue}
                    closeModal={() =>
                        updateWhichFilterModalIsShown("more filters")
                    }
                    resetFilters={resetAllStagedValues}
                    clearFilters={clearAllStagedValues}
                    runSearch={runSearchWithFiltersWrapper}
                />
            );
        }
    };

    const filterButtonClickWrapper = (
        ev: MouseEvent,
        filterCategory: FilterCategories
    ) => {
        ev.preventDefault();
        createAnalyticsEvent({
            origin: "web",
            platform: "web",
            product: "marketing",
            source: "none",
            userIdType: "atlassianAccount",
            eventComponent: "customerFilterButton",
            event: "clicked",
            eventType: "ui",
            action: "clicked",
            actionSubject: "component",
            attributes: {
                labelEnglish: filterCategory,
                url: location.href,
                page_path: location.pathname,
                eventComponent: "customerFilterButton"
            }
        }).fire();
        updateWhichFilterModalIsShown(filterCategory);
    };

    const buttonPropsList: Adg4ButtonProps[] = [
        {
            variant: "primary",
            color: "light_grey",
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onClick: (ev: any) => filterButtonClickWrapper(ev, "solution")
        },
        {
            variant: "primary",
            color: "light_grey",
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onClick: (ev: any) => filterButtonClickWrapper(ev, "product")
        },
        {
            variant: "primary",
            color: "light_grey",
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onClick: (ev: any) => filterButtonClickWrapper(ev, "industry")
        },
        {
            variant: "secondary",
            color: "light_grey",
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onClick: (ev: any) => filterButtonClickWrapper(ev, "more filters")
        }
    ];

    const totalNumberOfActiveFilters = useMemo(() => {
        return getActiveFiltersByFilterType("all", filterObject) || 0;
    }, [filterObject]);

    // If any filter modal is open, show the modal
    const showModal =
        Object.values(whichFilterModalIsOpenObject).filter(
            (isActiveValue) => isActiveValue
        ).length > 0;

    const handleDocumentClick = useCallback(
        (event: MouseEvent) => {
            if (showModal) {
                let parentNode = event.target as HTMLElement;

                while (parentNode && parentNode.nodeName !== "BODY") {
                    if (
                        parentNode.id ===
                        "customer-story-modal-component-parent"
                    ) {
                        return;
                    }
                    parentNode = parentNode.parentNode as HTMLElement;
                }
            }

            // If we are here, it means the click was outside the modal
            const isModalClosing = Object.keys(
                whichFilterModalIsOpenObject
            ).filter(
                (key) => whichFilterModalIsOpenObject[key as FilterCategories]
            );

            if (isModalClosing.length > 0) {
                updateWhichFilterModalIsOpenObject(() => ({
                    ...whichFilterModalIsOpenObjectDefaultState
                }));

                setFilterObjectValue((prevState: CustomerStoryFilterObject) => {
                    // eslint-disable-next-line react-hooks/exhaustive-deps
                    filterObject = {
                        ...prevState,
                        ...commitAllStagedActiveValues(prevState)
                    };
                    runSearchWithFilters(filterObject, searchStringValue);
                    return filterObject;
                });
            }
        },

        [showModal]
    );

    // Attach and detach the event listener for the document click
    // This is used to determine if there is a click outside of the filter modals
    useEffect(() => {
        // Attach the listeners on component mount.
        document.addEventListener("click", handleDocumentClick, false);
        // Detach the listeners on component unmount.
        return () => {
            document.removeEventListener("click", handleDocumentClick, false);
        };
    }, [handleDocumentClick]);

    return (
        <UFOSegment name="customers-page-filters">
            <TypographyStyles>
                <section css={customerStoriesSectionContainerStyle}>
                    <Row layout="12">
                        <div css={customerStoriesFilterContainerStyle}>
                            <div
                                css={[
                                    filterButtonContainerStyle,
                                    Object.keys(filterObject.industry)
                                        .length === 0
                                        ? css({ opacity: 0 })
                                        : undefined
                                ]}
                            >
                                {buttonPropsList.map((buttonProp, index) => {
                                    const filterKey = Object.keys(filterObject)[
                                        index
                                    ] as FilterCategories;
                                    // Inefficient, we should look into memoizing this
                                    const numFiltersActive: number =
                                        getActiveFiltersByFilterType(
                                            filterKey,
                                            filterObject
                                        );

                                    const buttonClassNameBuilder = `customer-story-filter-button-adg4 ${
                                        numFiltersActive ? "filter-active" : ""
                                    } ${
                                        whichFilterModalIsOpenObject[filterKey]
                                            ? "selected"
                                            : ""
                                    } ${
                                        filterKey === "more filters"
                                            ? "more-filters"
                                            : ""
                                    }`;

                                    const filterModal: JSX.Element | null =
                                        getFilterModal(showModal, filterKey);

                                    return (
                                        <div
                                            key={index}
                                            className="customer-story-filter-button"
                                        >
                                            <Adg4Button
                                                {...buttonProp}
                                                className={
                                                    buttonClassNameBuilder
                                                }
                                                id="customer-story-modal-component-parent"
                                                css={filterButtonStyles}
                                            >
                                                {/*@ts-ignore */}
                                                {localizedMap[filterKey]}{" "}
                                                {filterDropDownArrowSVG}
                                                <span
                                                    css={
                                                        filterButtonCountStyles
                                                    }
                                                >
                                                    {numFiltersActive}
                                                </span>
                                            </Adg4Button>
                                            {filterModal}
                                        </div>
                                    );
                                })}
                                <div
                                    css={mobileFilterButtonContainerStyles}
                                    id="customer-story-modal-component-parent"
                                >
                                    <button
                                        css={[
                                            mobileFilterButtonStyles,
                                            totalNumberOfActiveFilters
                                                ? mobileFilterButtonActiveStyles
                                                : undefined
                                        ]}
                                        id={"more filters"}
                                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                        onClick={(ev: any) =>
                                            filterButtonClickWrapper(
                                                ev,
                                                "more filters"
                                            )
                                        }
                                    >
                                        {/* eslint-disable-next-line no-restricted-syntax */}
                                        <img
                                            src="https://wac-cdn.atlassian.com/misc-assets/webp-images/customer-story-filter-icon-3367749555.webp"
                                            alt="Filter icon"
                                            css={mobileFilterButtonImgStyles}
                                        />
                                        <span
                                            css={[
                                                mobileFilterButtonTextStyles,
                                                totalNumberOfActiveFilters
                                                    ? mobileFilterHiddenStyles
                                                    : undefined
                                            ]}
                                        >
                                            {intl.formatMessage({
                                                id: "CustomersLandingPage.button.filters",
                                                defaultMessage: "Filters"
                                            })}
                                        </span>
                                        <span
                                            css={[
                                                mobileFilterButtonTextActiveStyles,
                                                totalNumberOfActiveFilters
                                                    ? mobileFilterInnerStyles
                                                    : undefined
                                            ]}
                                        >
                                            {totalNumberOfActiveFilters}
                                        </span>
                                    </button>
                                    {getFilterModal(showModal, "more filters")}
                                </div>
                            </div>
                            <div css={searchButtonContainerStyle}>
                                <CustomerStorySearchBar
                                    searchStringValue={searchStringValue}
                                    setSearchString={setSearchString}
                                    runSearch={() =>
                                        runSearchWithFilters(
                                            filterObject,
                                            searchStringValue
                                        )
                                    }
                                    searchInputFieldFocused={
                                        searchInputFieldFocused
                                    }
                                    setSearchInputFieldFocused={
                                        setSearchInputFieldFocused
                                    }
                                    clearSearchField={clearSearchField}
                                />
                            </div>
                        </div>
                    </Row>
                    {hasFilterSearchRun && (
                        <Row layout="12">
                            <div css={filterResultStyle}>
                                <h5 css={filterResultLabelStyle}>
                                    <FormattedMessage
                                        id="CustomersLandingPage.numberOfResults"
                                        defaultMessage="<numberOfCustomerStories></numberOfCustomerStories> results"
                                        values={{
                                            numberOfCustomerStories: () =>
                                                numberOfCustomerStoriesByCommittedValues
                                        }}
                                    />
                                </h5>
                                <button
                                    type="button"
                                    onClick={clearAllFilterButtonClickHandler}
                                    css={filterClearStyle}
                                >
                                    {intl.formatMessage({
                                        id: "CustomersLandingPage.clear",
                                        defaultMessage: "Clear"
                                    })}
                                </button>
                            </div>
                        </Row>
                    )}
                </section>
            </TypographyStyles>
        </UFOSegment>
    );
};

const filterDropDownArrowSVG = (
    <svg
        width="10"
        height="6"
        viewBox="0 0 10 6"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="customer-story-filter-dropdown-arrow"
    >
        <path
            d="M9 1.18164L5 4.818L1 1.18164"
            stroke="#101214"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const customerStoriesSectionContainerStyle = css({
    margin: "80px 0 32px",

    [media.above.xs]: {
        // Tech debt: Adding this disable to unblock commits for GWP-2646
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".row-layout": {
            marginBlockStart: "0"
        }
    },
    [media.below.sm]: {
        // Tech debt: Adding this disable to unblock commits for GWP-2646
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".row-layout": {
            paddingInline: "16px"
        }
    }
});

const customerStoriesFilterContainerStyle = css({
    display: "flex",
    flexWrap: "nowrap",
    justifyContent: "space-between"
});

const filterButtonContainerStyle = css({
    "position": "relative",
    "display": "flex",
    "flexWrap": "nowrap",
    "flexBasis": "fit-content",
    "transition": "opacity 200ms linear",

    [media.below.md]: {
        flexBasis: "15%"
    },
    // Tech debt: Adding this disable to unblock commits for GWP-2646
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".more-filters": {
        // Tech debt: Adding this disable to unblock commits for GWP-2646
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".customer-story-filter-dropdown-arrow": {
            display: "none"
        }
    }
});

const mobileFilterButtonContainerStyles = css({
    display: "none",
    [media.below.md]: {
        display: "block"
    }
});
const mobileFilterButtonStyles = css({
    "cursor": "pointer",
    "display": "flex",
    "justifyContent": "center",
    "alignItems": "center",
    "background": "none",
    "border": "none",
    "borderRadius": "100px",
    "height": "40px",
    "width": "100%",
    "color": "#101214",
    "padding": "8px 16px",
    "gap": "8px",
    ":hover": {
        // eslint-disable-next-line local/ensure-wac-token-usage
        background: "#F0F1F2"
    }
});

const mobileFilterButtonActiveStyles = css({
    "padding": "16px",
    "background": "#F8EEFE",
    ":hover, &.selected": {
        // eslint-disable-next-line local/ensure-wac-token-usage
        background: "#F8EEFE"
    }
});

const mobileFilterButtonImgStyles = css({
    width: "24px",
    height: "24px"
});
const mobileFilterButtonTextStyles = css({
    display: "inline-block"
});
const mobileFilterInnerStyles = css({
    display: "flex"
});
const mobileFilterHiddenStyles = css({
    display: "none"
});
const mobileFilterButtonTextActiveStyles = css({
    display: "none",
    justifyContent: "center",
    alignItems: "center",
    // eslint-disable-next-line local/ensure-wac-token-usage
    background: "#D8A0F7",
    borderRadius: "50%",
    margin: "0px",
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#101214",
    width: "24px",
    height: "24px",
    fontWeight: 600
});

const filterButtonStyles = css({
    "&.customer-story-filter-button-adg4": {
        textTransform: "capitalize",
        marginInlineEnd: "16px",
        paddingInline: "16px",
        cursor: "pointer",
        fontSize: "1rem",
        minWidth: "126px",
        fontWeight: 400
    },
    [media.below.md]: {
        display: "none"
    },
    // Tech debt: Adding this disable to unblock commits for GWP-2646
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".customer-story-filter-dropdown-arrow": {
        transition: "ease-in-out 0.3s transform",
        marginInlineStart: "8px"
    },
    // Tech debt: Adding this disable to unblock commits for GWP-2646
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "&.selected .customer-story-filter-dropdown-arrow": {
        transform: "rotate(-180deg)"
    },
    ":hover, &.selected": {
        // eslint-disable-next-line local/ensure-wac-token-usage
        background: "#F0F1F2"
    },
    "&.filter-active": {
        "background": "#F8EEFE",
        ":hover, &.selected": {
            // eslint-disable-next-line local/ensure-wac-token-usage
            background: "#F8EEFE"
        },
        // Tech debt: Adding this disable to unblock commits for GWP-2646
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "span": { display: "inline-block" },
        // Tech debt: Adding this disable to unblock commits for GWP-2646
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".customer-story-filter-dropdown-arrow": {
            display: "none"
        },
        "&.more-filters": {
            minWidth: "156px"
        }
    }
});

const filterButtonCountStyles = css({
    display: "none",
    alignContent: "center",
    // eslint-disable-next-line local/ensure-wac-token-usage
    background: "#D8A0F7",
    borderRadius: "50%",
    marginInlineStart: "8px",
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#101214",
    width: "20px",
    height: "20px",
    fontWeight: 600
});

const searchButtonContainerStyle = css({
    display: "flex",
    flexWrap: "nowrap",
    maxWidth: "564px",
    flexBasis: "45%",
    justifyContent: "flex-end",
    [media.below.md]: {
        maxWidth: "80%",
        flexBasis: "80%",
        justifyContent: "flex-end"
    }
});

const filterResultStyle = css({
    display: "flex",
    flexWrap: "nowrap",
    width: "1272px",
    maxWidth: "100%",
    margin: "22px auto 0",
    justifyContent: "flex-start",
    alignItems: "center",
    height: "28px",
    textAlign: "left"
});
const filterResultLabelStyle = css({
    margin: "0",
    marginInlineEnd: "16px",
    fontSize: "20px",
    fontWeight: 700,
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#101214"
});
const filterClearStyle = css({
    textDecoration: "underline",
    fontFamily: "Charlie Text",
    cursor: "pointer",
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#0065FF;",
    fontSize: "14px",
    background: "none",
    border: "0"
});
